import { useState, useEffect, useRef } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
    googleSignup,
    googleLogin,
    searchPlaces,
    createSession
} from '../api';
import {
    WEB_CLIENT_ID,
    RELIGIONS,
    MONTHS,
    DAYS,
    YEARS,
    COUNTRIES,
    INITIAL_BIRTH_DETAILS,
    INITIAL_SELECTED_COUNTRY,
    ERROR_MESSAGES,
    STORAGE_KEYS,
    SEARCH_DEBOUNCE_DELAY,
    MODAL_TRANSITION_DELAY,
    MOBILE_NUMBER_MIN_LENGTH,
    MOBILE_NUMBER_MAX_LENGTH
} from '../constants/chatConstants';
import BirthDetailsModal from './utils/BirthDetailsModal';
import WhySignUpModal from './utils/WhySignUpModal';
import MessagePopup from './utils/MessagePopup';

const SignupForm = ({ onSignupSuccess, onNeedLogin }) => {
    const navigate = useNavigate();
    const { setUser } = useAuth();

    // ================================
    // STATE DECLARATIONS
    // ================================
    const [isNative, setIsNative] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');

    // Auth State
    const [googleCredential, setGoogleCredential] = useState(null);
    const [googleUserInfo, setGoogleUserInfo] = useState(null);

    // Modal State
    const [showBirthDetailsPopup, setShowBirthDetailsPopup] = useState(false);
    const [showWhySignUpModal, setShowWhySignUpModal] = useState(false);
    const [showMessagePopup, setShowMessagePopup] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showTimePicker, setShowTimePicker] = useState(false);

    // Dropdown State
    const [showReligionDropdown, setShowReligionDropdown] = useState(false);
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [showPhoneCodeDropdown, setShowPhoneCodeDropdown] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Time Picker State
    const [selectedHour, setSelectedHour] = useState(null);
    const [selectedMinute, setSelectedMinute] = useState(null);

    // Birth Details
    const [birthDetails, setBirthDetails] = useState(INITIAL_BIRTH_DETAILS);

    // Location/Place State
    const [selectedCountry, setSelectedCountry] = useState(INITIAL_SELECTED_COUNTRY);
    const [placeCountries] = useState(COUNTRIES);
    const [phoneCountries] = useState(COUNTRIES);
    const [placeSuggestions, setPlaceSuggestions] = useState([]);
    const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPlace, setSelectedPlace] = useState(null);

    // Refs
    const searchTimeoutRef = useRef(null);

    // ================================
    // INITIALIZATION
    // ================================

    useEffect(() => {
        const checkPlatform = async () => {
            const nativePlatform = Capacitor.isNativePlatform?.() || false;
            setIsNative(nativePlatform);
        };
        checkPlatform();
    }, []);

    useEffect(() => {
        if (showMessagePopup && message && (message.includes('✅') || message.includes('successful'))) {
            const timer = setTimeout(() => {
                setShowMessagePopup(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showMessagePopup, message]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const refCode = urlParams.get('ref');

        if (refCode) {
            localStorage.setItem(STORAGE_KEYS.PENDING_REFERRAL, refCode);
            setMessage(`🎁 Welcome! Signing up with referral code: ${refCode}`);

            const url = new URL(window.location);
            url.searchParams.delete('ref');
            window.history.replaceState({}, '', url);
        }
    }, []);

    // ================================
    // HELPER FUNCTIONS
    // ================================

    const showPopupMessage = (msg) => {
        setMessage(msg);
        setShowMessagePopup(true);
    };

    const isValidDate = () => {
        const { birth_day, birth_month, birth_year } = birthDetails;
        if (!birth_day || !birth_month || !birth_year) return false;

        const day = parseInt(birth_day);
        const month = parseInt(birth_month);
        const year = parseInt(birth_year);
        const date = new Date(year, month - 1, day);

        return date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day;
    };

    // ================================
    // FORM HANDLERS
    // ================================

    const handleBirthDetailsChange = (field, value) => {
        if (field === 'country') {
            const country = COUNTRIES.find(c => c.name === value);
            setBirthDetails(prev => ({
                ...prev,
                country: value,
                country_code: country ? country.code : ''
            }));
        } else {
            setBirthDetails(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const clearBirthDetails = () => {
        setBirthDetails(INITIAL_BIRTH_DETAILS);
        setPlaceSuggestions([]);
        setShowSuggestions(false);
        setSelectedPlace(null);
        setSearchQuery('');
    };

    const toggleReligionDropdown = () => {
        setShowReligionDropdown(!showReligionDropdown);
    };

    const handleReligionSelect = (religion) => {
        handleBirthDetailsChange('religion', religion.value);
        setShowReligionDropdown(false);
    };

    const toggleCountryDropdown = () => {
        setShowCountryDropdown(!showCountryDropdown);
    };

    const handleCountrySelect = (country) => {
        setSelectedCountry(country);
        setShowCountryDropdown(false);

        setBirthDetails(prev => ({
            ...prev,
            country: country.name,
            country_code: country.code
        }));

        handleBirthDetailsChange('birth_place', '');
        setPlaceSuggestions([]);
        setSearchQuery('');
        setSelectedPlace(null);
    };

    // ================================
    // PLACE SEARCH
    // ================================

    const handleSearchQueryChange = async (value) => {
        setSearchQuery(value);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (!value || value.length < 2) {
            setPlaceSuggestions([]);
            return;
        }

        searchTimeoutRef.current = setTimeout(async () => {
            setIsLoadingPlaces(true);
            try {
                const response = await searchPlaces(value, selectedCountry.code.toUpperCase());
                if (response.data && response.data.success) {
                    setPlaceSuggestions(response.data.places || []);
                } else {
                    setPlaceSuggestions([]);
                }
            } catch (error) {
                console.error('❌ Place search error:', error);
                setPlaceSuggestions([]);
            } finally {
                setIsLoadingPlaces(false);
            }
        }, SEARCH_DEBOUNCE_DELAY);
    };

    const handlePlaceSelect = (place) => {
        try {
            setSelectedPlace(place);

            const country = COUNTRIES.find(c => c.code.toLowerCase() === place.country_code.toLowerCase());
            const timezoneAbbr = country?.abbreviation || 'IST';

            let formattedPlace;
            if (place.main_text && place.secondary_text) {
                formattedPlace = `${place.main_text.trim()}, ${place.secondary_text.trim()}`;
            } else {
                formattedPlace = place.description || place.formatted_address || place.main_text || 'Selected Location';
            }

            handleBirthDetailsChange('birth_place', formattedPlace);
            handleBirthDetailsChange('timezoneAbbr', timezoneAbbr);
            setShowSuggestions(false);
            setSearchQuery(formattedPlace);

            setTimeout(() => {
                setPlaceSuggestions([]);
            }, MODAL_TRANSITION_DELAY);
        } catch (error) {
            console.error('Error selecting place:', error);
            setPlaceSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handlePlaceKeyDown = (event, place) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handlePlaceSelect(place);
        } else if (event.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    const handlePlaceInputKeyDown = (event) => {
        if (event.key === 'Escape') {
            setShowSuggestions(false);
            setPlaceSuggestions([]);
        }
    };

    // ================================
    // REGISTRATION
    // ================================

    const handleRegistrationComplete = async () => {
        console.log('🚀 ========== REGISTRATION START ==========');

        if (!birthDetails.full_name || !birthDetails.full_name.trim()) {
            showPopupMessage(ERROR_MESSAGES.FULL_NAME);
            return;
        }

        if (!birthDetails.religion) {
            showPopupMessage(ERROR_MESSAGES.RELIGION);
            return;
        }

        if (!isValidDate()) {
            showPopupMessage(ERROR_MESSAGES.BIRTH_DATE);
            return;
        }

        if (!birthDetails.birth_time || !birthDetails.birth_time.trim()) {
            showPopupMessage(ERROR_MESSAGES.BIRTH_TIME);
            return;
        }

        if (!birthDetails.birth_place || !birthDetails.birth_place.trim() || !selectedPlace) {
            showPopupMessage(ERROR_MESSAGES.BIRTH_PLACE);
            return;
        }

        if (!birthDetails.mobile_number || !birthDetails.mobile_number.trim()) {
            showPopupMessage(ERROR_MESSAGES.MOBILE_NUMBER);
            return;
        }

        const cleanedNumber = birthDetails.mobile_number.replace(/\D/g, '');
        if (cleanedNumber.length < MOBILE_NUMBER_MIN_LENGTH || cleanedNumber.length > MOBILE_NUMBER_MAX_LENGTH) {
            showPopupMessage(ERROR_MESSAGES.INVALID_MOBILE);
            return;
        }

        setIsLoading(true);
        setMessage('');

        try {
            const birthDate = `${birthDetails.birth_year}-${birthDetails.birth_month.toString().padStart(2, '0')}-${birthDetails.birth_day.toString().padStart(2, '0')}`;
            const pendingReferralCode = localStorage.getItem(STORAGE_KEYS.PENDING_REFERRAL);

            const registrationData = {
                token: googleCredential,
                profile: {
                    full_name: birthDetails.full_name.trim(),
                    religion: birthDetails.religion,
                    birth_date: birthDate,
                    birth_time: birthDetails.birth_time,
                    birth_place: selectedPlace?.description || birthDetails.birth_place,
                    country_code: selectedPlace?.country_code || selectedCountry.code.toUpperCase(),
                    latitude: selectedPlace?.latitude || null,
                    longitude: selectedPlace?.longitude || null,
                    timezoneAbbr: birthDetails.timezoneAbbr || 'IST',
                    mobile_number: birthDetails.mobile_number,
                    country_code_no: birthDetails.country_code_no || '+91',
                    referredBy: pendingReferralCode || null
                }
            };

            console.log('📤 Registering user...');
            const response = await googleSignup(googleCredential, registrationData.profile);

            if (response.data.success) {
                console.log('✅ Registration successful!');
                localStorage.removeItem(STORAGE_KEYS.PENDING_REFERRAL);
                setShowBirthDetailsPopup(false);
                clearBirthDetails();
                setSelectedPlace(null);
                setGoogleUserInfo(null);

                console.log('🔑 Auto-logging in user...');
                setTimeout(async () => {
                    await handleAutoLoginAfterRegistration(googleCredential);
                }, 100);

            } else {
                throw new Error(response.data.error || 'Registration failed');
            }

        } catch (error) {
            console.error('=== REGISTRATION ERROR ===', error);

            if (error.response?.data) {
                const errorData = error.response.data;

                if (errorData.waitingList) {
                    // ✅ CLOSE MODAL for waiting list scenario
                    setShowBirthDetailsPopup(false);
                    
                    const userDataForModal = {
                        name: errorData.user?.fullname || errorData.user?.name || birthDetails.full_name,
                        email: errorData.user?.email || googleUserInfo?.email || 'N/A'
                    };
                    setGoogleUserInfo(userDataForModal);
                    
                    setTimeout(() => {
                        showPopupMessage(errorData.message || 'You are on the waiting list.');
                    }, 300);

                } else if (errorData.message) {
                    // ✅ KEEP MODAL OPEN for validation errors (like duplicate mobile number)
                    // User needs to fix the issue and resubmit
                    showPopupMessage(errorData.message);

                } else {
                    showPopupMessage('❌ Registration failed. Please try again.');
                }
            } else if (error.message) {
                showPopupMessage(error.message);
            } else {
                showPopupMessage(ERROR_MESSAGES.NETWORK_ERROR);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // ================================
    // AUTO-LOGIN AFTER REGISTRATION
    // ================================

    const handleAutoLoginAfterRegistration = async (credential) => {
        if (!credential) {
            setMessage('Auto-login failed. Please refresh and try again.');
            return;
        }

        setIsLoading(true);
        try {
            console.log('🔍 Attempting auto-login after registration...');

            const response = await googleLogin(credential);

            if (response.data.success) {
                console.log('✅ Auto-login successful!');
                const userData = response.data.user;
                const authToken = response.data.token;

                if (userData && authToken) {
                    localStorage.setItem('astroguru_token', authToken);
                    console.log('🔑 Token saved to localStorage for API calls');

                    await setUser(userData, authToken);

                    try {
                        const sessionResponse = await createSession(userData.userId);

                        if (sessionResponse?.data?.success) {
                            localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionResponse.data.session.sessionId);
                            localStorage.setItem(STORAGE_KEYS.USER_ID, userData.userId);
                            console.log('✅ Session created:', sessionResponse.data.session.sessionId);
                        }
                    } catch (sessionError) {
                        console.error('❌ Session creation error:', sessionError);
                    }

                    if (onSignupSuccess) {
                        onSignupSuccess(response.data);
                    }

                    setTimeout(() => navigate('/home'), 500);
                }
            }
        } catch (error) {
            console.log('❌ Auto-login error:', error);
            setMessage('Auto-login failed. Please login manually.');
        } finally {
            setIsLoading(false);
        }
    };

    // ================================
    // AUTHENTICATION
    // ================================

    const handleGoogleAuth = async (credentialResponse) => {
        if (!credentialResponse?.credential) {
            setMessage('Google authentication failed. Please try again.');
            return;
        }

        setIsLoading(true);

        try {
            setGoogleCredential(credentialResponse.credential);

            const base64Url = credentialResponse.credential.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            const userInfo = JSON.parse(jsonPayload);
            setGoogleUserInfo(userInfo);

            console.log('🔍 Checking if account exists...');
            try {
                const response = await googleLogin(credentialResponse.credential);
                if (response.data.success) {
                    console.log('✅ Account exists! Auto-logging in...');
                    const userData = response.data.user;
                    const authToken = response.data.token;

                    localStorage.setItem('astroguru_token', authToken);
                    console.log('🔑 Token saved to localStorage for API calls');

                    await setUser(userData, authToken);

                    try {
                        const sessionResponse = await createSession(userData.userId);
                        if (sessionResponse.data.success) {
                            localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionResponse.data.session.sessionId);
                            localStorage.setItem(STORAGE_KEYS.USER_ID, userData.userId);
                            console.log('✅ Session created:', sessionResponse.data.session.sessionId);
                        }
                    } catch (sessionError) {
                        console.error('❌ Session creation error:', sessionError);
                    }

                    if (onSignupSuccess) {
                        onSignupSuccess(response.data);
                    }

                    setTimeout(() => navigate('/home'), 500);
                    return;
                }
            } catch (error) {
                console.log('📝 Account does not exist - showing registration form');
            }

            setBirthDetails((prev) => ({ ...prev, full_name: userInfo.name }));
            setShowBirthDetailsPopup(true);

        } catch (error) {
            console.error('Authentication error:', error);
            setMessage('Authentication failed: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNativeGoogleAuth = async () => {
        setIsLoading(true);
        setMessage('');

        try {
            console.log('📱 Initializing native Google Auth...');

            await GoogleAuth.initialize({
                clientId: WEB_CLIENT_ID,
                scopes: ['profile', 'email'],
                grantOfflineAccess: true,
            });

            console.log('📱 Requesting Google sign-in...');
            const result = await GoogleAuth.signIn();

            const credential = result.authentication?.idToken || result.idToken;

            if (!credential) {
                throw new Error('No ID token received from Google Auth');
            }

            console.log('✅ Native Google Auth successful');
            await handleGoogleAuth({ credential });

        } catch (err) {
            console.error('❌ Native Google Auth Failed:', err);

            if (err.message?.includes('popup_closed_by_user') || err.message?.includes('SIGN_IN_CANCELLED')) {
                setMessage('⚠️ Sign-in was cancelled. Please try again.');
            } else if (err.message?.includes('NETWORK_ERROR')) {
                setMessage('❌ Network error. Please check your internet connection.');
            } else {
                setMessage(`❌ Google authentication failed: ${err.message || 'Unknown error'}`);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const closeBirthDetailsPopup = () => {
        setShowBirthDetailsPopup(false);
        clearBirthDetails();
        setGoogleCredential(null);
        setGoogleUserInfo(null);
        setMessage('');
    };

    return (
        <>
            <div className="">
                <div className="google-auth-container">
                    {isNative ? (
                        <button
                            className="google-native-btn"
                            onClick={handleNativeGoogleAuth}
                            disabled={isLoading}
                        >
                            <span>🌐</span>
                            {isLoading ? 'Signing up...' : 'Sign up with Google'}
                        </button>
                    ) : (
                        <GoogleLogin
                            onSuccess={handleGoogleAuth}
                            onError={(error) => {
                                console.error('❌ Web Google Auth Failed:', error);
                                setMessage('❌ Google authentication failed. Please try again.');
                            }}
                            theme="filled_blue"
                            size="large"
                            text="signup_with"
                            shape="rectangular"
                            width="relative"
                            disabled={isLoading}
                        />
                    )}
                </div>
                <button
                    onClick={() => setShowWhySignUpModal(true)}
                    className="preview-text"
                >
                    <div className="google-auth-container">
                        <div className="" style={{ minWidth: "200px" }}>
                            Why Join Us?
                        </div>
                    </div>
                </button>
                <p className="auth-info-text">
                    Secure signup with Google<br />
                    🌟 Where Stars Meet Strategy with AI🌟
                </p>
            </div>

            {message && (
                <div className={`status-message ${message.includes('❌') ? 'error' :
                        message.includes('⚠️') ? 'warning' : 'success'
                    }`}>
                    {message}
                </div>
            )}

            <BirthDetailsModal
                show={showBirthDetailsPopup}
                close={closeBirthDetailsPopup}
                isFromAuth={true}
                googleUserInfo={googleUserInfo}
                birthDetails={birthDetails}
                setBirthDetails={setBirthDetails}
                handleBirthDetailsChange={handleBirthDetailsChange}
                showDatePicker={showDatePicker}
                setShowDatePicker={setShowDatePicker}
                showTimePicker={showTimePicker}
                setShowTimePicker={setShowTimePicker}
                selectedHour={selectedHour}
                setSelectedHour={setSelectedHour}
                selectedMinute={selectedMinute}
                setSelectedMinute={setSelectedMinute}
                showReligionDropdown={showReligionDropdown}
                toggleReligionDropdown={toggleReligionDropdown}
                handleReligionSelect={handleReligionSelect}
                placeCountries={placeCountries}
                loadingCountries={false}
                showPhoneCodeDropdown={showPhoneCodeDropdown}
                setShowPhoneCodeDropdown={setShowPhoneCodeDropdown}
                selectedCountry={selectedCountry}
                showCountryDropdown={showCountryDropdown}
                toggleCountryDropdown={toggleCountryDropdown}
                handleCountrySelect={handleCountrySelect}
                showSuggestions={showSuggestions}
                setShowSuggestions={setShowSuggestions}
                placeSuggestions={placeSuggestions}
                isLoadingPlaces={isLoadingPlaces}
                searchQuery={searchQuery}
                handleSearchQueryChange={handleSearchQueryChange}
                handlePlaceSelect={handlePlaceSelect}
                handlePlaceKeyDown={handlePlaceKeyDown}
                handlePlaceInputKeyDown={handlePlaceInputKeyDown}
                isLoading={isLoading}
                handleRegistrationComplete={handleRegistrationComplete}
            />

            <WhySignUpModal
                showWhySignUpModal={showWhySignUpModal}
                setShowWhySignUpModal={setShowWhySignUpModal}
            />

            <MessagePopup
                showMessagePopup={showMessagePopup}
                message={message}
                setShowMessagePopup={setShowMessagePopup}
            />
        </>
    );
};

export default SignupForm;
